import React from 'react'
import { Box, CircularProgress, LinearProgress, Typography, keyframes } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`

type UploadStep = 'idle' | 'creating' | 'uploading' | 'confirming'

type Props = {
  step: UploadStep
  progress?: number
  fileName?: string
}

const stepMessages: Record<UploadStep, string> = {
  idle: '',
  creating: 'Preparando upload...',
  uploading: 'Enviando arquivo...',
  confirming: 'Finalizando...'
}

const stepProgress: Record<UploadStep, number> = {
  idle: 0,
  creating: 25,
  uploading: 60,
  confirming: 90
}

export default function UploadProgress({ step, progress, fileName }: Props) {
  if (step === 'idle') return null

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: 320,
        bgcolor: '#fff',
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        overflow: 'hidden',
        zIndex: 1300,
        animation: `${pulse} 2s ease-in-out infinite`
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1.5,
          bgcolor: '#1976d2',
          color: '#fff'
        }}
      >
        <CloudUploadIcon />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Enviando arquivo
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <CircularProgress size={20} sx={{ color: '#fff' }} />
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2 }}>
        {fileName && (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {fileName}
          </Typography>
        )}

        <Typography variant="caption" sx={{ color: '#666' }}>
          {stepMessages[step]}
        </Typography>

        <LinearProgress
          variant="determinate"
          value={progress ?? stepProgress[step]}
          sx={{
            mt: 1.5,
            height: 6,
            borderRadius: 3,
            bgcolor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              borderRadius: 3
            }
          }}
        />
      </Box>
    </Box>
  )
}
